import { useMutation } from '@tanstack/react-query';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createWritingQuestionIntoActivity,
  updateWritingQuestionIntoActivity,
} from '@/data/services/question/writingQuestionServices';
import Activity from '@/models/Activity';
import { Question, WritingQuestion } from '@/models/Question';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import alert from '@/utils/shared/alert';
import { isEqual } from 'lodash';
import WritingForm, { WritingFormFields } from './WritingForm';

type WritingFormServiceProps = {
  activity: Activity;
  question: Question;
  isActive?: boolean;
};

export default function WritingFormService({
  activity,
  question,
  isActive,
}: WritingFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);

  const handleCreate = async (
    data: WritingFormFields,
  ): Promise<WritingQuestion> => {
    const { maxGrade, ...rest } = data;
    const editableActivity = await ensureEditable();
    const saved = await createWritingQuestionIntoActivity(editableActivity.id, {
      type: question.type,
      identifier: question.identifier,
      order: question.order,
      ...rest,
      feedbacks: sanitizeFeedbacks(rest.feedbacks),
    });
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: WritingFormFields,
  ): Promise<WritingQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<WritingFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const changedFields: Partial<Omit<WritingFormFields, 'maxGrade'>> = {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      changedFields.context = rest.context;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const { updateTarget } = await resolveEditable(question as WritingQuestion);
    const saved = await updateTarget(
      changedFields,
      updateWritingQuestionIntoActivity,
    );

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({
        question: saved,
        originalQuestion: question,
        maxGrade,
      });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: WritingFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <WritingForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
